import DeviceMaintenanceRoute from 'app/routes/DeviceMaintenance.route';
import ExacqVisionVMSRoute from 'app/routes/ExacqVisionVMS.route';
import IntegrationsRoute from 'app/routes/Integrations.route';
import { ConfigProvider } from 'components/LocalConfigProvider/ConfigProvider';
import { AppSSEProvider } from 'contexts/AppSSEContext';
import { PermsProvider, usePerms } from 'contexts/PermsContext';
import { GetRouteData } from 'data/routeData';
import AuditLogsV2 from 'features/AuditLogs_v2/AuditLogs_v2.index';
import { AuthContext } from 'features/Auth/contexts/AuthContext';
import MaintenanceDevices from 'features/Integrations/features/MaintenanceDevices/index.web';
import useIntegrationEvents from 'features/Integrations/hooks/useIntegrationEvents';
import useIntegrationInit from 'features/Integrations/hooks/useIntegrationInit';
import { useAlarmEvents } from 'hooks/useAlarmEvents';
import useAlertsEvents from 'hooks/useAlertEvents';
import useRolePermsEvents from 'hooks/useRolePermsEvents';
import useIpawsEvents from 'features/Weather/features/WeatherAlerts/features/AlertCreator/hooks/useIpawsEvents';
import useCustomWeatherEvents from 'features/Weather/hooks/useCustomWeatherEvents';
import Cookies from 'js-cookie';
import { Page404 } from 'pages/404';
import { ForgotPassword } from 'pages/ForgotPassword';
import PolicyAndCookies from 'pages/Privacy/PolicyAndCookies';
import { PrivacyPopup } from 'pages/Privacy/PrivacyPopup';
import Redirect from 'pages/Redirect';
import ResetPassword from 'pages/ResetPassword';
import ResetPasswordRedirect from 'pages/ResetPasswordRedirect';
import TermsAndConditions from 'pages/TermsAndConditions';
import UserMenu from 'pages/UserMenu/UserMenu';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PiVideoFill } from 'react-icons/pi';
import { useDispatch, useSelector } from 'react-redux';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { getAccessLevel } from 'services/perms/perms.service';
import { getPolicyAndCookies, getTermsAndConditions } from 'services/policy/policy.service';
import { getUserProfileInfo } from 'services/user/user.service';
import { NAVIGATION_STYLES } from 'store/config/displayOptions.config';
import {
  selectAccessibilityOptions,
  selectResolvedDisplayTheme,
} from 'store/selectors/DisplayOptions/displayOptionsSelectors';
import {
  hydrateDisplayOptions,
  resetDisplayOptions,
} from 'store/slices/DisplayOptions/displayOptionsSlice';
import { addViewportResizeListeners } from 'utils/layout/viewportResize.utils';
import { getProfileDisplayOptions } from 'utils/preferences/userOptions.utils';
import { applyDisplayThemeToDocument } from 'utils/themes/themeDom.utils';
import './App.css';
import GetWidgetData from './data/widgetData/widgetData.js';
import AppLayout from './layouts/AppLayout';
import PublicLayout from './layouts/PublicLayout';
import ActivateAccount from './pages/ActivateAccount';
import Dashboard from './pages/Dashboard/Dashboard';
import Signin from './pages/Signin';
import Signup from './pages/Signup';
import Accessibility from 'features/UserMenu/features/Accessibility/index.web';

// Dev overlay — imported statically; the component returns null in production.
import { clearStaleSessionState } from 'utils/sessions/deployment.utils';
import DevRenderOverlay from './components/DevRenderOverlay/DevRenderOverlay.jsx';

export const PageContext = createContext();
export const SidebarContext = createContext(true);
export const MicrosoftContext = createContext();

const baseSize = 1000;
export const LayoutSizeContext = createContext({
  width: baseSize,
  height: window.innerHeight - 137,
  widgetWidth: baseSize / 3,
  doubleWidgetWidth: (baseSize / 3) * 2 + 9,
  widgetHeight: baseSize / 3,
  doubleWidgetHeight: baseSize / 3 + 100,
  scaleMult: 1,
  baseFontSize: 14,
});

//how many colors do we need?
//are there any colors that serve a specific purpose (ie. main background color (that 'dark-grey' color behind all of the pages/widgets))?
export const UniqueWidgetRefreshContext = createContext({
  base: 0,
  primary: 0,
  secondary: 0,
  tertiary: 0,
  fontColor: 'white',
});

export const SetUniqueWidgetRefreshContext = createContext(() => {});
export const PermissionContext = createContext({});
export const UserAgentContext = createContext({});

// Rendered inside AppSSEProvider so these hooks can call useAppSSE()
function SSEEventWatcher({ isAuthenticated }) {
  useIntegrationEvents({ enabled: isAuthenticated });
  useAlertsEvents({ enabled: isAuthenticated });
  useAlarmEvents({ enabled: isAuthenticated });
  useRolePermsEvents({ enabled: isAuthenticated });
  useIpawsEvents({ enabled: isAuthenticated });
  useCustomWeatherEvents({ enabled: isAuthenticated });
  return null;
}

function App() {
  // Dev-only render tracking
  try {
    if (process.env.NODE_ENV === 'development') {
      require('hooks/useRenderTracker').default('App');
    }
  } catch (e) {}

  const authContext = useContext(AuthContext);
  const { accessToken, isAuthenticated } = authContext;
  const dispatch = useDispatch();
  const accessibilityOptions = useSelector(selectAccessibilityOptions);
  const resolvedDisplayTheme = useSelector(selectResolvedDisplayTheme);
  const colors = resolvedDisplayTheme.value;

  useEffect(() => {
    applyDisplayThemeToDocument({
      accessibilityOptions,
      resolvedDisplayTheme,
    });
  }, [accessibilityOptions, resolvedDisplayTheme]);

  useEffect(() => {
    let canceled = false;

    if (!isAuthenticated) {
      return () => {
        canceled = true;
      };
    }

    const hydrateProfileOptions = async () => {
      try {
        const response = await getUserProfileInfo();
        if (canceled) return;

        const profileOptions = response?.data?.result?.options ?? null;
        const profileDisplayOptions = getProfileDisplayOptions(profileOptions);

        if (profileDisplayOptions) {
          dispatch(hydrateDisplayOptions(profileDisplayOptions));
        } else {
          dispatch(resetDisplayOptions());
        }
      } catch (error) {}
    };

    hydrateProfileOptions();

    return () => {
      canceled = true;
    };
  }, [dispatch, isAuthenticated]);

  useIntegrationInit({ enabled: isAuthenticated }); // Initializes integrations state and fetches necessary data on app load
  const [microsoftContextValue, setMicrosoftContextValue] = useState({
    hasClientId: false,
  });
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [showFull, setShowFull] = useState(() => {
    const cookiesAccepted = Cookies.get('cookiesAgreed') === 'true';
    const privacyAccepted = Cookies.get('privacyAgreed') === 'true';
    return !(cookiesAccepted && privacyAccepted);
  });
  const [updateSidebar] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [routeName, setRouteName] = useState({
    name: 'dashboard',
    displayName: 'Home Dashboard',
  });
  const [accessLevel, setAccessLevel] = useState(-1);
  const [search, setSearch] = useState('');
  const [widgetRefresh, setWidgetRefresh] = useState('');
  const [cookiesAgreed, setCookiesAgreed] = useState(() => Cookies.get('cookiesAgreed') === 'true');
  const [privacyAgreed, setPrivacyAgreed] = useState(() => Cookies.get('privacyAgreed') === 'true');
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [newTermsFullyAgreed, setNewTermsFullyAgreed] = useState(false);
  const [hasSignedIn, setHasSignedIn] = useState(false);
  const [termsFullyViewed, setTermsFullyViewed] = useState(false);
  const [termsData, setTermsData] = useState({ version: '', page: '' });
  const [policyData, setPolicyData] = useState({ version: '', page: '' });
  const [showSmallMenu, setShowSmallMenu] = useState(true);
  const hasAcceptedPolicy = privacyAgreed && cookiesAgreed;

  useEffect(() => {
    const fetchTermsAndConditions = async () => {
      try {
        const termsData = await getTermsAndConditions();
        if (termsData.status !== 200) {
          // alert("Unable to connect to the server. Please try again later.");

          return;
        }
        setTermsData({ ...termsData.data, status: termsData.status });
      } catch (error) {
        console.error('Error fetching terms and conditions:', error);
        setTermsData({ version: '', page: '', status: 500 });
      }
    };

    const fetchPolicyAndCookies = async () => {
      try {
        const policyData = await getPolicyAndCookies();
        if (policyData?.status !== 200) {
          // alert("Unable to connect to the server. Please try again later.");

          return;
        }
        setPolicyData({ ...policyData.data, status: policyData.status });
      } catch (error) {
        console.error('Error fetching policy and cookies:', error);
        setPolicyData({ version: '', page: '', status: 500 });
      }
    };

    fetchTermsAndConditions();
    fetchPolicyAndCookies();
  }, []);

  useEffect(() => {
    if (!policyData?.version) return;

    const acceptedVersion = Cookies.get('policyAgreed');
    if (acceptedVersion === policyData.version) {
      if (!cookiesAgreed || !privacyAgreed) {
        setCookiesAgreed(true);
        setPrivacyAgreed(true);
      }
      Cookies.set('cookiesAgreed', 'true', { expires: 365 });
      Cookies.set('privacyAgreed', 'true', { expires: 365 });
      return;
    }

    if (hasAcceptedPolicy) {
      Cookies.set('policyAgreed', policyData.version, { expires: 365 });
      Cookies.set('cookiesAgreed', 'true', { expires: 365 });
      Cookies.set('privacyAgreed', 'true', { expires: 365 });
      return;
    }

    Cookies.remove('policyAgreed');
    Cookies.remove('cookiesAgreed');
    Cookies.remove('privacyAgreed');
    if (cookiesAgreed || privacyAgreed) {
      setCookiesAgreed(false);
      setPrivacyAgreed(false);
    }
  }, [policyData?.version, hasAcceptedPolicy, cookiesAgreed, privacyAgreed]);

  const divStyle = {
    color: colors.fontColor,
    width: '100%',
  };

  const initialWidth = Math.max(window.innerWidth - (sidebarOpen ? 295 : 100), 1000);

  const [layoutObject, setLayoutObject] = useState({
    width: initialWidth,
    height: window.innerHeight - 137,
    widgetWidth: initialWidth / 3,
    doubleWidgetWidth: (initialWidth / 3) * 2 + 9,
    widgetHeight: initialWidth / 3,
    doubleWidgetHeight: initialWidth / 3 + 100,
    scaleMult: 1,
    baseFontSize: 14,
  });

  const [uwfValues, setUwfValues] = useState({
    base: 0,
  });

  const lastLayoutRef = useRef(null);
  const lastDimensionsRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const newSize = Math.max(window.innerWidth - (sidebarOpen ? 295 : 100), 1000);

      const newSizeHeight = window.innerHeight - 107;

      const newLayoutObject = {
        width: newSize * 0.9875,
        height: newSizeHeight,
        widgetWidth: newSize / 3,
        doubleWidgetWidth: (newSize / 3) * 2 + 9,
        widgetHeight: newSize / 3,
        doubleWidgetHeight: (newSize / 3) * 2 + 9,
        scaleMult: newSize / baseSize,
        baseFontSize: Math.min(Math.max(((16 * newSize) / baseSize) * 0.75, 14), 30),
      };

      const newDimensions = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      // Only update if values actually changed
      if (JSON.stringify(lastLayoutRef.current) !== JSON.stringify(newLayoutObject)) {
        lastLayoutRef.current = newLayoutObject;
        setLayoutObject(newLayoutObject);
      }

      if (JSON.stringify(lastDimensionsRef.current) !== JSON.stringify(newDimensions)) {
        lastDimensionsRef.current = newDimensions;
        setDimensions(newDimensions);
      }
    };
    const removeViewportResizeListeners = addViewportResizeListeners(handleResize);

    handleResize();
    return () => {
      removeViewportResizeListeners();
    };
  }, [sidebarOpen]);

  return (
    <AppSSEProvider enabled={isAuthenticated}>
      <SSEEventWatcher isAuthenticated={isAuthenticated} />
      <ContextProvider
        colors={colors}
        sidebarOpen={sidebarOpen}
        layoutObject={layoutObject}
        microsoftContextValue={microsoftContextValue}
        setMicrosoftContextValue={setMicrosoftContextValue}
        uwfValues={uwfValues}
        setUwfValues={setUwfValues}
        accessToken={accessToken}
      >
        <AppRoutingChildren
          colors={colors}
          dimensions={dimensions}
          showFull={showFull}
          setShowFull={setShowFull}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          mobileNavOpen={mobileNavOpen}
          setMobileNavOpen={setMobileNavOpen}
          routeName={routeName}
          setRouteName={setRouteName}
          accessLevel={accessLevel}
          setAccessLevel={setAccessLevel}
          search={search}
          setSearch={setSearch}
          widgetRefresh={widgetRefresh}
          setWidgetRefresh={setWidgetRefresh}
          cookiesAgreed={cookiesAgreed}
          setCookiesAgreed={setCookiesAgreed}
          privacyAgreed={privacyAgreed}
          setPrivacyAgreed={setPrivacyAgreed}
          termsAgreed={termsAgreed}
          setTermsAgreed={setTermsAgreed}
          newTermsFullyAgreed={newTermsFullyAgreed}
          setNewTermsFullyAgreed={setNewTermsFullyAgreed}
          hasSignedIn={hasSignedIn}
          setHasSignedIn={setHasSignedIn}
          termsFullyViewed={termsFullyViewed}
          setTermsFullyViewed={setTermsFullyViewed}
          termsData={termsData}
          policyData={policyData}
          showSmallMenu={showSmallMenu}
          setShowSmallMenu={setShowSmallMenu}
          hasAcceptedPolicy={hasAcceptedPolicy}
          divStyle={divStyle}
          updateSidebar={updateSidebar}
        />
      </ContextProvider>
    </AppSSEProvider>
  );
}

export default App;

const AppRoutingChildren = ({
  colors,
  dimensions,
  showFull,
  setShowFull,
  sidebarOpen,
  setSidebarOpen,
  mobileNavOpen,
  setMobileNavOpen,
  routeName,
  setRouteName,
  accessLevel,
  setAccessLevel,
  search,
  setSearch,
  widgetRefresh,
  setWidgetRefresh,
  cookiesAgreed,
  setCookiesAgreed,
  privacyAgreed,
  setPrivacyAgreed,
  termsAgreed,
  setTermsAgreed,
  newTermsFullyAgreed,
  setNewTermsFullyAgreed,
  hasSignedIn,
  setHasSignedIn,
  termsFullyViewed,
  setTermsFullyViewed,
  termsData,
  policyData,
  showSmallMenu,
  setShowSmallMenu,
  hasAcceptedPolicy,
  updateSidebar,
}) => {
  // Get access to perms context
  const perms = usePerms();

  // State that depends on perms context
  const [routes, setRoutes] = useState([]);
  const [pagesLoaded, setPagesLoaded] = useState(false);
  const [hasFetchedPerms, setHasFetchedPerms] = useState(false);
  const authContext = useContext(AuthContext);
  const { isAuthenticated, isInitialized, logout: authLogout } = authContext;

  // Use auth context's initialization state instead of separate loading
  const isAuthLoading = !isInitialized || isAuthenticated === null;

  useEffect(() => {
    if (isInitialized || pagesLoaded) return;

    // Safety net: if auth or page load has not completed within 15 seconds,
    // the app is stuck in a half-initialized state (e.g. a stale token that
    // the server never responded to, or a broken permissions fetch).
    // Clear all browser-side state and reload so the user lands on the login
    // screen rather than spinning indefinitely.
    const timeout = setTimeout(() => {
      if (!isInitialized || (isAuthenticated && !pagesLoaded)) {
        clearStaleSessionState();
        window.location.reload();
      }
    }, 15000); // 15 second safety-net (reduced from 60s)

    return () => clearTimeout(timeout);
  }, [isInitialized, pagesLoaded, isAuthenticated]);

  useEffect(() => {
    // Only fetch permissions when authenticated
    if (!isAuthenticated) {
      perms.reset();
      return;
    }

    const fetchPermsAndLevel = async () => {
      try {
        const res = await getAccessLevel();
        if (res.status === 200) {
          const result = res?.data?.result;
          // Accept falsy values like 0 or false — only reject null/undefined
          if (
            result &&
            result.group_id != null &&
            result.maintenanceOperatorLevel != null &&
            result.serviceManager !== undefined
          ) {
            perms.setAccessLevel(result.group_id);
            perms.setMaintenanceLevel(result.maintenanceOperatorLevel);
            perms.setServiceManagerFlag(result.serviceManager);
            perms.setDeploymentManagerFlag(Boolean(result.deploymentManager));
            setAccessLevel(result.group_id);
            perms.get();
          } else {
            throw new Error(
              `Missing parameter in fetchPermsAndLevel response: ${
                result?.group_id ?? 'undefined'
              } || ${result?.maintenanceOperatorLevel ?? 'undefined'} || ${
                result?.serviceManager ?? 'undefined'
              }`,
            );
          }
        }
      } catch (error) {
        console.error('Error fetching access level:', error);
        perms.reset();
        // Auth context will handle logout via interceptor
      }
    };

    if (!hasFetchedPerms) {
      fetchPermsAndLevel();
      setHasFetchedPerms(true);
    }
  }, [isAuthenticated, hasFetchedPerms]);

  // Effects that depend on perms context
  useEffect(() => {
    const cookie = perms.get();
    if (!cookie || !isAuthenticated) {
      return;
    }
    let allowed = [];

    const appdata = [...GetRouteData(perms, widgetRefresh)];

    appdata.forEach((item) => {
      if (item.permission()) {
        item.options.forEach((option) => {
          //set enabled by perm
          if (option.permission()) {
            allowed.push(option);
          }
        });
      }
    });
    //This is a bandaid Fix, do not reuse
    try {
      if (
        perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewExacqVisionOverwatchVMSConnections'])
      ) {
        allowed.push({
          name: 'Overwatch VMS',
          icon: () => {
            return <PiVideoFill />;
          },
          enabled: true,
          href: '/vms',
          permission: () =>
            perms.testPermission(
              [8, 9, 10, 11, 12, 13],
              ['ViewExacqVisionOverwatchVMSConnections'],
            ),
          page: <ExacqVisionVMSRoute />,
        });
      }

      if (perms.maintenanceLevel > 0) {
        allowed.push({
          name: 'Maintenance Devices',
          icon: () => {
            return;
          },
          enabled: true,
          href: '/maintenancedashboard',
          permission: () => perms.maintenanceLevel > 0,
          page: <MaintenanceDevices />,
        });
      }
    } catch (error) {
      console.error('Error setting routes based on permissions:', error);
    }

    setRoutes(allowed);
    setPagesLoaded(true);
  }, [perms.data, perms.maintenanceLevel, updateSidebar, isAuthenticated, widgetRefresh]);

  useEffect(() => {
    if (!isAuthenticated) perms.reset();
    if (isAuthenticated) setShowFull(false);
  }, [isAuthenticated]);

  const definedWidgetData = useMemo(
    () => GetWidgetData(widgetRefresh, setWidgetRefresh, sidebarOpen),
    [widgetRefresh, sidebarOpen],
  );

  return (
    <>
      {isAuthLoading || (isAuthenticated && !pagesLoaded) ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100vw',
            height: '100vh',
            backgroundColor: colors.background,
            color: colors.fontColor,
          }}
        >
          <div className="text-center">
            <div className="spinner-border" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-3">
              {isAuthLoading ? 'Authenticating...' : 'Loading application...'}
            </div>
          </div>
        </div>
      ) : (
        <>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            {showSmallMenu && (
              <PrivacyPopup
                hasAcceptedPolicy={hasAcceptedPolicy}
                setShowFull={setShowFull}
                showFull={showFull}
                setMobileNavOpen={setMobileNavOpen}
                setHasFetchedPerms={setHasFetchedPerms}
                setCookiesAgreed={setCookiesAgreed}
                setPrivacyAgreed={setPrivacyAgreed}
                setNewTermsFullyAgreed={setNewTermsFullyAgreed}
                setTermsFullyViewed={setTermsFullyViewed}
                setHasSignedIn={setHasSignedIn}
                policyData={policyData}
              />
            )}
            <Routes>
              {/* Redirect to login */}
              {isAuthenticated === false && (
                <Route element={<PublicLayout />}>
                  <Route path="*" element={<Redirect route={'/'} />} />
                  <Route
                    path="/"
                    element={
                      <Signin
                        setShowFull={setShowFull}
                        hasSignedIn={hasSignedIn}
                        setHasSignedIn={setHasSignedIn}
                        widgetRefresh={widgetRefresh}
                        setCookiesAgreed={setCookiesAgreed}
                        setPrivacyAgreed={setPrivacyAgreed}
                        setTermsAgreed={setTermsAgreed}
                        setNewTermsFullyAgreed={setNewTermsFullyAgreed}
                        newTermsFullyAgreed={newTermsFullyAgreed}
                        cookiesAgreed={cookiesAgreed}
                        privacyAgreed={privacyAgreed}
                        termsAgreed={termsAgreed}
                        termsVersion={termsData?.version}
                        policyVersion={policyData?.version}
                        termsDate={termsData?.date}
                        terms={termsData?.page}
                        termsStatus={termsData?.status}
                        policyStatus={policyData?.status}
                        termsFullyViewed={termsFullyViewed}
                        setTermsFullyViewed={setTermsFullyViewed}
                      />
                    }
                  />
                  <Route
                    path="/activate"
                    element={
                      <PageContentWrapper>
                        <ActivateAccount
                          setCookiesAgreed={setCookiesAgreed}
                          setPrivacyAgreed={setPrivacyAgreed}
                        />
                      </PageContentWrapper>
                    }
                  />
                  <Route
                    path="/resetPassword"
                    element={
                      <PageContentWrapper>
                        <ResetPassword
                          setCookiesAgreed={setCookiesAgreed}
                          setPrivacyAgreed={setPrivacyAgreed}
                          setShowSmallMenu={setShowSmallMenu}
                        />
                      </PageContentWrapper>
                    }
                  />
                  <Route
                    path="/forgotPassword"
                    element={
                      <PageContentWrapper>
                        <ForgotPassword setShowSmallMenu={setShowSmallMenu} />
                      </PageContentWrapper>
                    }
                  />
                  <Route
                    path="/passwordRedirect"
                    element={
                      <PageContentWrapper>
                        <ResetPasswordRedirect />
                      </PageContentWrapper>
                    }
                  />
                  <Route
                    path="/terms"
                    element={
                      <PageContentWrapper>
                        <TermsAndConditions
                          data={termsData}
                          setShowFull={setShowFull}
                          setShowSmallMenu={setShowSmallMenu}
                        />
                      </PageContentWrapper>
                    }
                  />
                  <Route
                    path="/policy"
                    element={
                      <PageContentWrapper allowPageScroll>
                        <PolicyAndCookies
                          data={policyData}
                          setShowFull={setShowFull}
                          setShowSmallMenu={setShowSmallMenu}
                        />
                      </PageContentWrapper>
                    }
                  />
                </Route>
              )}

              {isAuthenticated && (
                <>
                  <Route path="/" element={<Redirect route={'/dashboard'} />} />
                  <Route
                    element={
                      <AppLayout
                        setSidebarOpen={setSidebarOpen}
                        routeName={routeName}
                        accessLevel={accessLevel}
                        updateSidebar={updateSidebar}
                        widgetRefresh={widgetRefresh}
                        setWidgetRefresh={setWidgetRefresh}
                        sidebarOpen={sidebarOpen}
                        setMobileNavOpen={setMobileNavOpen}
                        mobileNavOpen={mobileNavOpen}
                        setShowFull={setShowFull}
                        setHasFetchedPerms={setHasFetchedPerms}
                        setCookiesAgreed={setCookiesAgreed}
                        setPrivacyAgreed={setPrivacyAgreed}
                        setNewTermsFullyAgreed={setNewTermsFullyAgreed}
                        setTermsFullyViewed={setTermsFullyViewed}
                        setHasSignedIn={setHasSignedIn}
                      />
                    }
                  >
                    <Route
                      path="/dashboard"
                      element={
                        <Dashboard
                          dimensions={dimensions}
                          sidebarOpen={sidebarOpen}
                          search={search}
                          definedWidgetData={definedWidgetData}
                          accessLevel={accessLevel}
                        />
                      }
                    />
                    {perms.maintenanceLevel > 0 && (
                      <Route
                        path="/maintenancedashboard"
                        element={
                          <PageContentWrapper>
                            <DeviceMaintenanceRoute />
                          </PageContentWrapper>
                        }
                      />
                    )}
                    <Route
                      path="/usermenu"
                      element={
                        <PageContentWrapper>
                          <UserMenu />
                        </PageContentWrapper>
                      }
                    />
                    <Route
                      path="/system/auditlogs"
                      element={
                        <PageContentWrapper>
                          <AuditLogsV2 />
                        </PageContentWrapper>
                      }
                    />
                    <Route
                      path="/system/integrations"
                      element={
                        <PageContentWrapper allowPageScroll>
                          <IntegrationsRoute />
                        </PageContentWrapper>
                      }
                    />
                    <Route
                      path="/system/devices"
                      element={
                        <PageContentWrapper allowPageScroll>
                          <IntegrationsRoute />
                        </PageContentWrapper>
                      }
                    />
                    <Route
                      path="/accessibility"
                      element={
                        <PageContentWrapper>
                          <Accessibility appearance={NAVIGATION_STYLES.MERLIN} />
                        </PageContentWrapper>
                      }
                    />
                    {perms.testPermission(
                      [8, 9, 10, 11, 12, 13],
                      ['ViewExacqVisionOverwatchVMSConnections'],
                    ) && (
                      <Route
                        path="/vms"
                        element={
                          <PageContentWrapper>
                            <ExacqVisionVMSRoute />
                          </PageContentWrapper>
                        }
                      />
                    )}
                    <Route
                      path="/terms"
                      element={
                        <PageContentWrapper>
                          <TermsAndConditions
                            data={termsData}
                            setShowFull={setShowFull}
                            setShowSmallMenu={setShowSmallMenu}
                          />
                        </PageContentWrapper>
                      }
                    />
                    <Route
                      path="/policy"
                      element={
                        <PageContentWrapper allowPageScroll>
                          <PolicyAndCookies
                            setShowFull={setShowFull}
                            setShowSmallMenu={setShowSmallMenu}
                            data={policyData}
                          />
                        </PageContentWrapper>
                      }
                    />
                    {routes.length > 0 &&
                      routes
                        .filter((f) => f.page && f.permission())
                        .map((route, index) => (
                          <Route
                            key={index}
                            path={route.href}
                            element={
                              route.href === '/weather/maps' ? (
                                <div className="d-flex flex-column customScrollbar-X customScrollbar-Y">
                                  {route.page}
                                </div>
                              ) : (
                                <PageContentWrapper
                                  allowPageScroll={route.href === '/overwatch/servicemanager'}
                                >
                                  {route.page}
                                </PageContentWrapper>
                              )
                            }
                          />
                        ))}
                    <Route
                      path="/activate"
                      element={
                        <PageContentWrapper>
                          <ActivateAccount
                            setCookiesAgreed={setCookiesAgreed}
                            setPrivacyAgreed={setPrivacyAgreed}
                          />
                        </PageContentWrapper>
                      }
                    />
                    <Route
                      path="/register"
                      element={
                        <PageContentWrapper>
                          <Signup />
                        </PageContentWrapper>
                      }
                    />
                  </Route>

                  {routes.length > 0 && pagesLoaded && (
                    <>
                      <Route path="*" element={<Page404 />} />
                    </>
                  )}
                </>
              )}
            </Routes>
          </Router>
        </>
      )}
      <ToastContainer
        // Toasts sit on --z-toast, above every modal level. With autoClose={false} a toast
        // that renders underneath an overlay is never seen at all. See Docs/Layering.md.
        style={{ '--toastify-z-index': 'var(--z-toast)' }}
        draggable // Allow drag to dismiss
        pauseOnHover // Pause timer on hover
        autoClose={false}
        position={dimensions.width > 768 ? 'top-right' : 'top-center'}
        toastStyle={{ width: 'min(90dvw, 400px)', marginTop: '10px' }}
        theme="dark"
      />
    </>
  );
};

const PageContentWrapper = ({ children, allowPageScroll = false }) => {
  return (
    <div
      className={`page-content-wrapper d-flex flex-column text-white h-100 w-100 customScrollbar-X customScrollbar-Y bg-base p-0 p-md-3 rounded-0 rounded-md-3 ${
        allowPageScroll ? 'page-content-scrollable' : ''
      }`}
    >
      {children}
    </div>
  );
};

const ContextProvider = ({
  children,
  colors,
  sidebarOpen,
  layoutObject,
  microsoftContextValue,
  setMicrosoftContextValue,
  uwfValues,
  setUwfValues,
  userAgent,
}) => {
  return (
    <ConfigProvider theme={{ token: { colorPrimary: colors.primaryShadow } }}>
      <MicrosoftContext.Provider
        value={{
          get: () => microsoftContextValue,
          set: setMicrosoftContextValue,
        }}
      >
        <UserAgentContext.Provider value={userAgent}>
          <SidebarContext.Provider value={sidebarOpen}>
            <LayoutSizeContext.Provider value={layoutObject}>
              <UniqueWidgetRefreshContext.Provider value={uwfValues}>
                <SetUniqueWidgetRefreshContext.Provider value={setUwfValues}>
                  <PermsProvider>
                    {children}
                    <DevRenderOverlay />
                  </PermsProvider>
                </SetUniqueWidgetRefreshContext.Provider>
              </UniqueWidgetRefreshContext.Provider>
            </LayoutSizeContext.Provider>
          </SidebarContext.Provider>
        </UserAgentContext.Provider>
      </MicrosoftContext.Provider>
    </ConfigProvider>
  );
};
